import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Stack, Text, Group, Badge, Button, ActionIcon, Tooltip,
  Alert, Skeleton, Timeline as MantineTimeline,
  ThemeIcon, Box, Modal, Textarea, Anchor, Menu,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconArrowLeft, IconAlertCircle, IconCircle, IconChevronUp, IconChevronDown,
  IconCheck, IconPlayerPlay, IconRefresh, IconArrowBackUp,
  IconHistory, IconArrowRight, IconMessage,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { chamadosApi } from '@/api/chamados'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'
import { formatApiError } from '@/api/errors'
import { useAuthStore, useCan } from '@/store/auth'
import { StatusBadge, SlaIndicator, CustomerValidationPanel, CommentThread, InfraContextPanel, EnvironmentChainProgress, TerraformPedidoPanel, PainelPedidoDeOrganizacao } from '@/components/chamados'
import type { EnvironmentChainResponse } from '@/api/chamados'
import { AttachmentList } from '@/components/chamados/AttachmentList'
import { SubTaskList } from '@/components/chamados/SubTaskList'
import { IniciarAtendimentoIaButton, type AtendimentoIa } from '@/components/chamados/IniciarAtendimentoIaButton'
import { FinalizarComIaItem } from '@/components/chamados/FinalizarComIaItem'
import { EncerrarAtendimentoIaModal } from '@/components/chamados/EncerrarAtendimentoIaModal'
import { TramitarChamadoModal } from '@/components/chamados/TramitarChamadoModal'
import { DestinoDeAtendimento, corpoDoEncaminhar, type LadoAtendimento } from '@/components/chamados/DestinoDeAtendimento'
import { mensagemDeAtendimento, rotuloDoAtendimento, type AtendimentoEscolhido } from '@/components/chamados/AtendidoPor'
import { CartaoDeLeitura } from '@/components/chamados/leitura/CartaoDeLeitura'
import { RemetenteDoChamado, formatarData } from '@/components/chamados/leitura/RemetenteDoChamado'
import { useAcoesDoChamado } from '@/components/chamados/leitura/useAcoesDoChamado'
import { BotaoFechar, FaixaProximoPasso, MenuMaisAcoes, type AcaoDaEsteira } from '@/components/chamados/leitura/FaixaProximoPasso'
import { acoesDaEsteira, type AcaoDeEsteira } from '@/components/chamados/leitura/esteira'
import { hrefDaEntrada, origemDoEstado, vizinhosNaPasta } from '@/components/chamados/leitura/vizinhos'
import { WidgetCard } from '@/components/ui/WidgetCard'
import { destinoDoVoltar } from './destinoDoVoltar'
import { notaDoEvento } from './notaDoEvento'

interface ChamadoDetail {
  id: number
  /** Spec 2026-10-16 §4.1: o estado do atendimento com IA, para o botão. */
  atendimento_ia?: AtendimentoIa | null
  tenant_id: number
  title: string
  description: string
  status: string
  request_type: string
  priority: string
  protocol_number: string | null
  requested_by_user_id: number
  requested_by_name: string
  origin?: string | null
  assignee: { id: number; full_name: string } | null
  category: {
    id: number
    name: string
    sla_first_response_hours: number | null
    sla_resolution_hours: number | null
  } | null
  resource_type: string | null
  resource_id: string | null
  resource_name: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  resolved_at: string | null
  closed_at: string | null
  /** Prazo da validação do solicitante (96h, TASK-2789). */
  validation_due_at?: string | null
  created_at: string
  updated_at: string
  custom_fields: Record<string, unknown> | null
  decision_reason: string | null
  /** Ambiente atual na esteira de promoção (sub-projeto A). */
  current_environment?: string | null
  /** Time/Equipe destinatário do chamado (permission_templates.id) — MIGRATION_099. */
  target_template_id?: number | null
  /** Nome do time/template destinatário, resolvido via JOIN no backend. */
  target_template_name?: string | null
  /** Quem pede e quem atende (TASK-12181). `null` quando o backend não conseguiu resolver. */
  lados?: {
    solicitante: { tenant_id: number; tenant_nome: string | null; org_nome: string | null } | null
    atendimento: LadoAtendimento | null
  } | null
  /** Entrada Mailbox §6.1: quem iniciou a triagem nesta chegada, ou null. */
  triagem_iniciada_por_nome?: string | null
  /** Entrada por etapa da triagem §4.6: a triagem foi concluída nesta chegada. */
  triagem_concluida?: boolean
  events: Array<{
    id: number
    actor_name: string
    event_type: string
    from_status: string | null
    to_status: string | null
    note: string | null
    created_at: string
  }>
}

function eventIcon(event_type: string) {
  switch (event_type) {
    case 'created':       return <IconCircle size={12} />
    case 'status_changed': return <IconPlayerPlay size={12} />
    case 'decision':      return <IconCheck size={12} />
    case 'assignment':    return <IconRefresh size={12} />
    case 'tramitado':     return <IconArrowRight size={12} />
    case 'comment':       return <IconMessage size={12} />
    default:              return <IconCircle size={12} />
  }
}

function eventColor(event_type: string): string {
  if (event_type === 'decision') return 'green'
  if (event_type === 'status_changed') return 'blue'
  if (event_type === 'comment') return 'teal'
  return 'gray'
}

// Rola até o comentário referenciado pelo evento de histórico (note='comment_id:N').
// Se o comentário exato não estiver visível (ex.: interno sem permissão), rola até
// a seção de comentários.
function scrollToComment(note: string | null) {
  const m = note?.match(/comment_id:(\d+)/)
  const target =
    (m && document.getElementById(`comment-${m[1]}`)) ||
    document.getElementById('chamado-comentarios')
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

// Responder (spec 2026-10-21 §3.5): rola até a Conversa e põe o foco no campo de resposta.
function irParaResposta() {
  const conversa = document.getElementById('chamado-comentarios')
  conversa?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  conversa?.querySelector<HTMLTextAreaElement>('textarea')?.focus({ preventScroll: true })
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return formatarData(iso)
}

const FECHADO = ['closed', 'rejected']

export function ChamadoDetalhe() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, selectedTenantId, setSelectedTenant, setSelectedOrg } = useAuthStore()
  const can = useCan()
  const isAdmin = user?.role === 'admin'
  // Espelha o guard do DELETE de comentario: `_require_caller_is_admin` chama
  // `rbac.is_admin_user`, que e' `has_permission(..., 'admin', 'full')` — flag
  // `is_admin`, role `admin`, template ou concessao direta. `isAdmin` sozinho
  // (role === 'admin') e' MAIS ESTREITO e esconderia o botao de admin de
  // plataforma que chega pela flag ou por template.
  const canDeleteComment = can('admin', 'full') || isAdmin

  // Endpoint tenant-less (spec §8.4.10) — resolve tenant via DB + permission
  // check multi-via (view_global / view_org+member / owner / assignee /
  // chamados:read). Necessario porque em escopo global o usuario abre chamados
  // de tenants diferentes do `selectedTenantId` do Sidebar, e o endpoint
  // legacy `/v1/tenants/{tid}/workflow/{id}` exigia membership do tenant
  // selecionado, gerando 404 em deep-links cross-tenant (2026-05-18).
  const { data, isLoading, error } = useQuery<ChamadoDetail>({
    queryKey: ['chamado', id],
    queryFn: async () => {
      const res = await api.get<ChamadoDetail>(`/v1/chamados/${id}`)
      return res.data
    },
    enabled: !!id,
    retry: false,
  })

  // Sub-recursos (comments, attachments, etc.) ainda usam endpoints
  // tenant-scoped — derivamos o tenant do proprio chamado, NAO do Sidebar.
  const chamadoTenantId = data?.tenant_id

  // Anterior e próximo na pasta de origem (spec 2026-10-21 §4). A chave começa por
  // `chamados-inbox` para `invalidarPendenciasDoChamado` também alcançá-la.
  const origem = origemDoEstado(location.state)
  const pastaDeOrigem = useQuery({
    queryKey: ['chamados-inbox', 'vizinhos', origem?.pasta, origem?.q ?? null, origem?.org ?? null, origem?.fila ?? null,
      origem?.ordem ?? null, origem?.dir ?? null],
    queryFn: async () => (await chamadosApi.getPasta({
      pasta: origem!.pasta, q: origem!.q || undefined, org: origem!.org ?? null, fila: origem!.fila ?? null,
      ordem: origem!.ordem ?? null, direcao: origem!.dir ?? null, limit: 500,
    })).data,
    enabled: origem != null,
    staleTime: 30_000,
  })
  const vizinhos = vizinhosNaPasta((pastaDeOrigem.data?.items ?? []).map((i) => i.id), Number(id))
  const irPara = (outro: number) => navigate(`/chamados/${outro}`, { state: location.state, replace: true })
  const voltar = () => {
    if (origem) {
      navigate(hrefDaEntrada(origem))
      return
    }
    const destino = destinoDoVoltar(location.key)
    if (destino === -1) navigate(-1)
    else navigate(destino)
  }

  // Entrada Mailbox §5.2: abrir o chamado registra a leitura (o negrito da Entrada sai
  // dela). Uma vez por chamado, sem bloquear a tela; falha não incomoda quem lê.
  const marcadoLidoRef = useRef<string | null>(null)
  useEffect(() => {
    if (!id || marcadoLidoRef.current === id) return
    marcadoLidoRef.current = id
    // `Promise.resolve().then`: nem um erro síncrono do cliente derruba a tela.
    Promise.resolve()
      .then(() => api.post(`/v1/chamados/${id}:mark-read`))
      .catch((err: unknown) => {
        console.warn('[ChamadoDetalhe] não foi possível registrar a leitura', err)
      })
  }, [id])

  // TASK-2473 / spec §9.4.2: ao montar com chamado de outro tenant (deep link
  // ou navegacao a partir de listagem org/global), sincroniza selectedTenantId
  // com o tenant do chamado e notifica o usuario da mudanca de contexto.
  // Guard contra loop com a reconciliação de tenant da Sidebar (React #185):
  // troca no máximo UMA vez por tenant de chamado.
  const autoSwitchedTenantRef = useRef<number | null>(null)
  useEffect(() => {
    if (!data?.tenant_id) return
    if (selectedTenantId === data.tenant_id) return
    if (autoSwitchedTenantRef.current === data.tenant_id) return
    autoSwitchedTenantRef.current = data.tenant_id
    // Zera a org de contexto (modo união) p/ a reconciliação da Sidebar NÃO
    // reverter o tenant do chamado — senão os dois efeitos entram em loop infinito.
    setSelectedOrg(null)
    setSelectedTenant(data.tenant_id)
    const tenantName =
      user?.tenants?.find((t) => t.id === data.tenant_id)?.name ?? `#${data.tenant_id}`
    notifications.show({
      title: 'Tenant ativo alterado',
      message: `Agora visualizando como tenant ${tenantName}`,
      color: 'blue',
    })
  }, [data?.tenant_id, selectedTenantId, setSelectedTenant, setSelectedOrg, user?.tenants])

  const { data: commentsData } = useQuery<{ id: number; author_user_id: number; author_name: string; is_internal: boolean; body: string; created_at: string }[]>({
    queryKey: ['chamado-comments', chamadoTenantId, Number(id)],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${chamadoTenantId}/workflow/${id}/comments`)
      return res.data as any
    },
    enabled: !!chamadoTenantId && !!id,
    retry: false,
  })

  // Cadeia de ambientes da mudança (sub-projeto A). Só faz fetch quando temos
  // o tenant do chamado. Backend é a autoridade da posição atual.
  const { data: chainData } = useQuery<EnvironmentChainResponse>({
    queryKey: ['chamado-chain', chamadoTenantId, Number(id)],
    queryFn: async () => {
      const res = await chamadosApi.getChain(chamadoTenantId!, Number(id))
      return res.data
    },
    enabled: !!chamadoTenantId && !!id,
    retry: false,
  })

  if (isLoading) {
    return (
      <Stack gap="md" role="status" aria-label="Carregando detalhes do chamado">
        <Skeleton height={40} />
        <Skeleton height={200} />
        <Skeleton height={150} />
      </Stack>
    )
  }

  if (error || !data) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        Não foi possível carregar o chamado. Verifique sua conexão e tente novamente.
      </Alert>
    )
  }

  const temCadeia = !!chainData && chainData.chain.length > 0

  return (
    <Stack gap="md">
      {/* Faixa de validação (spec 2026-09-27 §6.2): no topo, largura cheia, com o
          prazo. Renderiza só para o solicitante em validação. */}
      {chamadoTenantId && (
        <CustomerValidationPanel
          taskRequestId={data.id}
          tenantId={chamadoTenantId}
          isOwner={user?.id === data.requested_by_user_id}
          status={data.status}
          validationDueAt={data.validation_due_at ?? null}
        />
      )}

      {/* Trilha de ambientes (sub-projeto A). As ações dela estão na faixa Próximo passo. */}
      {temCadeia && (
        <WidgetCard p="md">
          <EnvironmentChainProgress
            chain={chainData.chain}
            currentEnvironment={chainData.current_environment ?? data.current_environment ?? null}
          />
        </WidgetCard>
      )}

      {/* Chave pelo chamado: ↑ ↓ trocam o chamado sem desmontar a página, e o estado dos
          formulários (nota, reclassificar…) não pode passar de um chamado para outro. */}
      <LeituraDoChamado
        key={data.id}
        data={data}
        temCadeia={temCadeia}
        ambiente={chainData?.current_environment ?? data.current_environment}
        navegacao={
          <Group gap={4} wrap="nowrap">
            <Tooltip label="Voltar">
              <ActionIcon variant="outline" size="md" aria-label="Voltar" onClick={voltar}>
                <IconArrowLeft size={16} />
              </ActionIcon>
            </Tooltip>
            {origem && (
              <>
                <Tooltip label="Chamado anterior">
                  <ActionIcon
                    variant="outline" size="md" aria-label="Chamado anterior"
                    disabled={vizinhos.anterior == null}
                    onClick={() => vizinhos.anterior != null && irPara(vizinhos.anterior)}
                  >
                    <IconChevronUp size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Próximo chamado">
                  <ActionIcon
                    variant="outline" size="md" aria-label="Próximo chamado"
                    disabled={vizinhos.proximo == null}
                    onClick={() => vizinhos.proximo != null && irPara(vizinhos.proximo)}
                  >
                    <IconChevronDown size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        }
      />

      {/* Pedido de Terraform: onde quem aprova decide (spec 2026-10-01 D1) */}
      {data.origin === 'terraform_runner' && (
        <TerraformPedidoPanel taskRequestId={data.id} />
      )}

      {/* Pedido de Nova Organização: decide pela rota dele (spec 2026-10-27 §4.6). O chamado do
          pedido é `approval`; sem pedido a rota responde 404 e o painel some. */}
      {data.request_type === 'approval' && (
        <PainelPedidoDeOrganizacao taskRequestId={data.id} />
      )}

      {/* Contexto de Infraestrutura */}
      {data.resource_id && chamadoTenantId && (
        <WidgetCard p="md">
          <InfraContextPanel
            taskRequestId={data.id}
            tenantId={chamadoTenantId}
            resourceType={data.resource_type}
            resourceId={data.resource_id}
            resourceName={data.resource_name}
          />
        </WidgetCard>
      )}

      {/* Sub-tarefas */}
      {chamadoTenantId && (
        <WidgetCard p="md">
          <SubTaskList
            parentTaskId={Number(id)}
            tenantId={chamadoTenantId}
            canCreate={!FECHADO.includes(data.status)}
          />
        </WidgetCard>
      )}

      {/* Conversa */}
      {chamadoTenantId && (
        <Box id="chamado-comentarios">
        <WidgetCard p="md">
          <CommentThread
            comments={commentsData ?? []}
            taskRequestId={Number(id)}
            tenantId={chamadoTenantId}
            canViewInternal={
              isAdmin
              || can('chamados', 'approve')
              || can('chamados', 'assign')
              || data.assignee?.id === user?.id
            }
            canComment={!FECHADO.includes(data.status)}
            canDelete={canDeleteComment}
          />
        </WidgetCard>
        </Box>
      )}

      {/* Histórico */}
      <WidgetCard p="md">
        <Text fw={600} size="sm" mb="md">Histórico</Text>
        {data.events.length === 0 ? (
          <Stack align="center" gap="xs" py="md">
            <IconHistory size={32} color="var(--ai-text-secondary)" stroke={1.5} />
            <Text size="sm" c="var(--ai-text-secondary)">Nenhum evento registrado neste chamado ainda.</Text>
          </Stack>
        ) : (
          // TASK-10274: o backend devolve em ordem de id (mais antigo primeiro); a tela
          // mostra o mais recente no topo, e so' ele fica "ativo".
          <MantineTimeline active={0} bulletSize={20} lineWidth={2}>
            {[...data.events].reverse().map((ev) => (
              <MantineTimeline.Item
                key={ev.id}
                bullet={
                  <ThemeIcon size={20} radius="xl" color={eventColor(ev.event_type)} variant="light">
                    {eventIcon(ev.event_type)}
                  </ThemeIcon>
                }
                title={
                  <Group gap="xs">
                    <Text size="xs" fw={500}>{ev.actor_name}</Text>
                    {ev.from_status && ev.to_status && (
                      <Group gap={4}>
                        <StatusBadge status={ev.from_status} size="xs" />
                        <Text size="xs" c="dimmed">→</Text>
                        <StatusBadge status={ev.to_status} size="xs" />
                      </Group>
                    )}
                  </Group>
                }
              >
                {ev.event_type === 'comment' ? (
                  <Anchor
                    size="xs"
                    onClick={() => scrollToComment(ev.note)}
                    style={{ cursor: 'pointer' }}
                  >
                    Comentário adicionado — ver
                  </Anchor>
                ) : notaDoEvento(ev.note) ? (
                  <Text size="xs" c="dimmed">{notaDoEvento(ev.note)}</Text>
                ) : null}
                <Text size="xs" c="dimmed">{formatDate(ev.created_at)}</Text>
              </MantineTimeline.Item>
            ))}
          </MantineTimeline>
        )}
      </WidgetCard>
    </Stack>
  )
}

interface LeituraDoChamadoProps {
  data: ChamadoDetail
  temCadeia: boolean
  ambiente: string | null | undefined
  navegacao: React.ReactNode
}

/**
 * O cartão de leitura de um chamado carregado (spec 2026-10-21 §3): o remetente, o corpo, a
 * faixa Próximo passo e o rodapé. As ações do atendente vêm de um `useAcoesDoChamado` só,
 * que a faixa e o rodapé dividem.
 */
function LeituraDoChamado({ data, temCadeia, ambiente, navegacao }: LeituraDoChamadoProps) {
  const id = String(data.id)
  const { user } = useAuthStore()
  const can = useCan()
  const isAdmin = user?.role === 'admin'
  const queryClient = useQueryClient()
  const fechado = FECHADO.includes(data.status)

  const acoes = useAcoesDoChamado({
    chamado: data,
    tenantId: data.tenant_id,
    isAdmin,
    userId: user?.id,
    podeAtribuir: can('chamados', 'assign'),
    // O gate de antes do painel de triagem, sem mudar quem vê.
    habilitado: can('chamados', 'assign') || can('chamados', 'approve') || isAdmin,
  })

  // Estado do modal de encaminhamento (Task 12 — chamados inbox plan).
  const [routeModalOpen, setRouteModalOpen] = useState(false)
  const [novoDestino, setNovoDestino] = useState<AtendimentoEscolhido | null>(null)
  const [motivo, setMotivo] = useState('')
  // Tramitar para outro tenant (spec 2026-09-30-tramitar-chamado): o botão só
  // aparece quando o backend devolve algum destino para quem está vendo.
  const [tramitarOpen, setTramitarOpen] = useState(false)
  // Encerrar atendimento com IA (spec 2026-10-16 §4.2): o modal mora aqui, fora do menu.
  const [encerrarIaOpen, setEncerrarIaOpen] = useState(false)

  const { data: tramitarOpcoes } = useQuery({
    queryKey: ['chamado-tramitar-destinos', id],
    queryFn: async () => (await chamadosApi.getTramitarDestinos(data.id)).data,
  })

  const routeMutation = useMutation({
    mutationFn: async () => {
      // Desde a TASK-12181 o destino é Organização e fila, pelo `encaminhar` (e não mais o
      // PATCH de time). É o único Encaminhar da tela (spec 2026-10-21 §3.5).
      if (!novoDestino) return
      await api.patch(`/v1/tenants/${data.tenant_id}/workflow/${data.id}/encaminhar`,
        corpoDoEncaminhar(novoDestino, motivo.trim()))
    },
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        title: 'Encaminhado',
        message: 'Chamado encaminhado com sucesso.',
      })
      queryClient.invalidateQueries({ queryKey: ['chamado', id] })
      invalidarPendenciasDoChamado(queryClient)
      setRouteModalOpen(false)
      setMotivo('')
      setNovoDestino(null)
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: mensagemDeAtendimento(
          (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail,
        ) ?? formatApiError(err, 'Falha ao encaminhar.'),
      })
    },
  })

  // Ações contextuais de promoção. Cada uma recarrega o chamado + a cadeia e
  // mostra notificação (403 de segregação → mensagem clara do backend).
  const workflowMutation = useMutation({
    mutationFn: async (action: AcaoDeEsteira) => {
      const tid = data.tenant_id
      const cid = data.id
      switch (action) {
        case 'approve':  return chamadosApi.approveRequest(tid, cid)
        case 'execute':  return chamadosApi.executeChange(tid, cid)
        case 'validate': return chamadosApi.validateExecution(tid, cid)
        case 'signoff':  return chamadosApi.signoffExecution(tid, cid)
        case 'promote':  return chamadosApi.promoteChange(tid, cid)
      }
    },
    onSuccess: (_res, action) => {
      notifications.show({ color: 'teal', title: 'Feito', message: 'Ação registrada com sucesso.' })
      queryClient.invalidateQueries({ queryKey: ['chamado', id] })
      // Promover muda o tenant do chamado: a chave antiga refaria a cadeia no tenant de
      // onde ele saiu (403). A chave nova nasce quando o chamado recarrega.
      if (action !== 'promote') {
        queryClient.invalidateQueries({ queryKey: ['chamado-chain', data.tenant_id, data.id] })
      }
      invalidarPendenciasDoChamado(queryClient)
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Não permitido',
        message: formatApiError(err, 'Falha ao executar a ação.'),
      })
    },
  })

  // As ações da esteira são de quem vê a cadeia, como antes: sem gate no front.
  const esteira: AcaoDaEsteira[] = temCadeia
    ? acoesDaEsteira(data, ambiente).map((a) => ({
      label: a.label,
      onClick: () => workflowMutation.mutate(a.act),
      ocupado: workflowMutation.isPending,
    }))
    : []

  const podeEncaminhar = can('chamados', 'assign') || acoes.pode.encaminhar
  const podeTramitar = !!tramitarOpcoes && tramitarOpcoes.destinos.length > 0

  const detalhes: { rotulo: string; valor: React.ReactNode }[] = []
  if (data.resource_id) {
    detalhes.push({
      rotulo: 'Recurso afetado',
      valor: (
        <Group gap="xs">
          <Badge size="xs" variant="outline" color="gray">{data.resource_type?.toUpperCase()}</Badge>
          <Text size="sm" ff="monospace">{data.resource_name ?? data.resource_id}</Text>
          <Text size="xs" c="var(--ai-text-secondary)" ff="monospace">({data.resource_id})</Text>
        </Group>
      ),
    })
  }
  if (data.scheduled_start) {
    detalhes.push({
      rotulo: 'Janela de execução',
      valor: `${formatDate(data.scheduled_start)} até ${formatDate(data.scheduled_end)}`,
    })
  }
  if (data.decision_reason) detalhes.push({ rotulo: 'Motivo da decisão', valor: data.decision_reason })
  for (const [chave, valor] of Object.entries(data.custom_fields ?? {})) {
    detalhes.push({ rotulo: chave, valor: String(valor ?? '—') })
  }

  return (
    <>
      <CartaoDeLeitura
        rotulo={`Chamado #${data.id}`}
        titulo={data.title}
        navegacao={navegacao}
        remetente={
          <RemetenteDoChamado
            chamado={data}
            status={
              <StatusBadge
                status={data.status}
                withIcon
                ctx={{
                  // Spec Entrada Mailbox §6: no detalhe o rótulo diz com quem o chamado está.
                  visao: user?.id === data.requested_by_user_id ? 'solicitante' : 'atendimento',
                  triagemIniciadaPor: data.triagem_iniciada_por_nome,
                  triagemConcluida: data.triagem_concluida,
                  responsavel: data.assignee?.full_name,
                  fila: data.lados?.atendimento?.fila_nome,
                  encaminhado: data.events?.some((ev) => ev.event_type === 'forwarded'),
                }}
              />
            }
            sla={data.category?.sla_resolution_hours ? (
              <SlaIndicator
                createdAt={data.created_at}
                slaResolutionHours={data.category.sla_resolution_hours}
                resolvedAt={data.resolved_at}
                closedAt={data.closed_at}
                taskRequestId={data.id}
                tenantId={data.tenant_id}
              />
            ) : undefined}
          />
        }
        proximoPasso={<FaixaProximoPasso acoes={acoes} esteira={esteira} />}
        rodape={
          <Group justify="space-between" gap="xs" wrap="wrap">
            <Group gap="xs" wrap="wrap">
              {!fechado && (
                <Button leftSection={<IconArrowBackUp size={14} />} onClick={irParaResposta}>
                  Responder
                </Button>
              )}
              {podeEncaminhar && (
                <Button
                  variant="outline"
                  leftSection={<IconArrowRight size={14} />}
                  onClick={() => setRouteModalOpen(true)}
                >
                  Encaminhar
                </Button>
              )}
              <IniciarAtendimentoIaButton
                chamado={data}
                variant="outline"
                acoes={can('chamados', 'assign') ? (
                  <>
                    <FinalizarComIaItem chamado={data} />
                    <Menu.Item color="red" onClick={() => setEncerrarIaOpen(true)}>
                      Encerrar atendimento com IA
                    </Menu.Item>
                  </>
                ) : undefined}
              />
            </Group>
            <Group gap="xs" wrap="wrap">
              {podeTramitar && (
                <Button variant="outline" onClick={() => setTramitarOpen(true)}>
                  Tramitar
                </Button>
              )}
              <MenuMaisAcoes acoes={acoes} />
              <BotaoFechar acoes={acoes} />
            </Group>
          </Group>
        }
      >
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {data.description || <Text span c="var(--ai-text-secondary)" size="sm">Sem descrição.</Text>}
        </Text>

        {detalhes.length > 0 && (
          <Box mt="lg" data-testid="detalhes-do-chamado">
            <Text size="xs" fw={600} c="var(--ai-text-secondary)" tt="uppercase" mb="xs">Detalhes</Text>
            <Stack gap={6}>
              {detalhes.map((d) => (
                <Group key={d.rotulo} gap="md" align="baseline" wrap="wrap" style={{ rowGap: 2 }}>
                  <Text size="xs" c="var(--ai-text-secondary)" w={{ base: '100%', sm: 160 }} style={{ flexShrink: 0 }}>{d.rotulo}</Text>
                  <Box style={{ minWidth: 0 }}>
                    {typeof d.valor === 'string' ? <Text size="sm">{d.valor}</Text> : d.valor}
                  </Box>
                </Group>
              ))}
            </Stack>
          </Box>
        )}

        <Box mt="lg">
          <AttachmentList
            taskRequestId={data.id}
            tenantId={data.tenant_id}
            canUpload={!fechado}
          />
        </Box>
      </CartaoDeLeitura>

      <EncerrarAtendimentoIaModal
        chamado={data}
        opened={encerrarIaOpen}
        onClose={() => setEncerrarIaOpen(false)}
      />

      <TramitarChamadoModal
        chamadoId={data.id}
        opened={tramitarOpen}
        onClose={() => setTramitarOpen(false)}
        onDone={() => {
          setTramitarOpen(false)
          notifications.show({ color: 'teal', title: 'Tramitado', message: 'Chamado tramitado para o novo tenant.' })
          queryClient.invalidateQueries({ queryKey: ['chamado', id] })
          queryClient.invalidateQueries({ queryKey: ['chamado-tramitar-destinos'] })
          invalidarPendenciasDoChamado(queryClient)
        }}
      />

      {/* Modal de encaminhamento — Task 12 (chamados inbox plan). */}
      <Modal
        opened={routeModalOpen}
        onClose={() => setRouteModalOpen(false)}
        title={`Encaminhar chamado #${data.id}`}
        size="md"
      >
        <Stack>
          <Group gap="xs">
            <Text size="sm" c="dimmed">De:</Text>
            <Badge variant="light" color={data.lados?.atendimento ? 'indigo' : 'gray'}>
              {data.lados?.atendimento
                ? rotuloDoAtendimento(data.lados.atendimento.org_nome, data.lados.atendimento.fila_nome)
                : data.target_template_name ?? 'Sem Organização atendente'}
            </Badge>
          </Group>
          <DestinoDeAtendimento
            label="Para a Organização e fila"
            tenantId={data.tenant_id}
            chamadoId={data.id}
            value={novoDestino}
            onChange={setNovoDestino}
          />
          <Textarea
            label="Motivo (opcional)"
            placeholder="Por que está encaminhando?"
            value={motivo}
            onChange={(e) => setMotivo(e.currentTarget.value)}
            rows={3}
            maxLength={500}
            description={`${motivo.length}/500`}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRouteModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={!novoDestino}
              loading={routeMutation.isPending}
              onClick={() => routeMutation.mutate()}
              leftSection={<IconArrowRight size={14} />}
            >
              Encaminhar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
